import React, { useState } from "react";
import axios from "axios";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSocialLinks } from "../../hooks/useSocialLinks";
import { SOCIAL_ICONS, SOCIAL_NAMES } from "../../constants/socialIcons";
import { Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const { t, language } = useLanguage();
  const { socialLinks: dynamicLinks } = useSocialLinks();
  const [email, setEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState("");

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setNewsletterStatus("success");
      setEmail("");
      setTimeout(() => setNewsletterStatus(""), 3000);
    } catch (err) {
      setNewsletterStatus("error");
      setTimeout(() => setNewsletterStatus(""), 3000);
    }
  };

  // Get enabled social links
  const enabledLinks = Object.entries(dynamicLinks).filter(([_, data]) => data?.enabled && data?.url);

  return (
    <footer className="bg-black border-t border-white/10 pb-24 md:pb-0" data-testid="footer">
      {/* Social Banner */}
      <div className="bg-gradient-to-r from-orange-600 via-orange-500 to-yellow-500 py-6">
        <div className="calli-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-lg font-bold text-white">
              {language === 'fr' ? '📱 Suivez-nous pour ne rien manquer !' : '📱 Follow us to stay updated!'}
            </p>
            <div className="flex items-center gap-2">
              {enabledLinks.map(([key, data]) => (
                <a
                  key={key}
                  href={data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/20 hover:bg-white hover:text-orange-500 flex items-center justify-center text-white font-bold transition-all hover:scale-110"
                  data-testid={`social-footer-${key}`}
                  title={SOCIAL_NAMES[key]}
                >
                  {SOCIAL_ICONS[key]}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="calli-container py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="text-3xl font-black bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Calli.
            </h3>
            <p className="text-gray-400">{t("footer_tagline")}</p>
            <div className="flex items-center gap-2 text-sm text-orange-400 font-semibold">
              <Shield className="w-4 h-4" />
              <span>{language === 'fr' ? 'Sécurisé & Sans écran' : 'Secure & Screen-free'}</span>
            </div>
          </div>

          {/* Links - Product */}
          <div>
            <h4 className="font-bold mb-4 text-orange-400">{t("footer_product")}</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/#features" className="hover:text-white transition-colors">{language === 'fr' ? 'Fonctionnalités' : 'Features'}</a></li>
              <li><a href="/#how-it-works" className="hover:text-white transition-colors">{language === 'fr' ? 'Comment ça marche' : 'How it works'}</a></li>
              <li><a href="/#preorder" className="hover:text-white transition-colors">{language === 'fr' ? 'Précommander' : 'Preorder'}</a></li>
              <li><a href="/#faq" className="hover:text-white transition-colors">{language === 'fr' ? 'Questions fréquentes' : 'FAQ'}</a></li>
              <li><a href="/app-parent" className="hover:text-white transition-colors">{language === 'fr' ? 'Application parent' : 'Parent app'}</a></li>
              <li><a href="/accessoires" className="hover:text-white transition-colors">{language === 'fr' ? 'Accessoires' : 'Accessories'}</a></li>
            </ul>
          </div>

          {/* Links - Company */}
          <div>
            <h4 className="font-bold mb-4 text-orange-400">{t("footer_company")}</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/notre-histoire" className="hover:text-white transition-colors">{language === 'fr' ? 'Notre histoire' : 'Our story'}</a></li>
              <li><a href="/equipe" className="hover:text-white transition-colors">{language === 'fr' ? 'L\'équipe' : 'The team'}</a></li>
              <li><a href="/presse" className="hover:text-white transition-colors">{language === 'fr' ? 'Presse & Médias' : 'Press & Media'}</a></li>
              <li><a href="/contact" className="hover:text-white transition-colors">{language === 'fr' ? 'Nous contacter' : 'Contact us'}</a></li>
              <li><a href="/carrieres" className="hover:text-white transition-colors">{language === 'fr' ? 'Carrières' : 'Careers'}</a></li>
              <li><a href="/blog" className="hover:text-white transition-colors">{language === 'fr' ? 'Blog' : 'Blog'}</a></li>
            </ul>
            {/* Founder direct line */}
            <div className="mt-4 pt-4 border-t border-gray-800">
              <p className="text-xs text-gray-500 mb-1">
                {language === 'fr' ? '📱 Ligne directe du fondateur' : '📱 Founder direct line'}
              </p>
              <a href="tel:+33642253607" className="text-orange-400 hover:text-orange-300 font-medium transition-colors">
                +33 6 42 25 36 07
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-bold mb-4 text-orange-400">{t("footer_newsletter")}</h4>
            <p className="text-gray-400 text-sm mb-3">
              {language === 'fr' ? 'Recevez les dernières news !' : 'Get the latest news!'}
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer_newsletter_placeholder")}
                className="w-full px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                data-testid="newsletter-input"
                required
              />
              <button 
                type="submit"
                className={`w-full font-bold py-3 rounded-xl transition-all ${
                  newsletterStatus === "success" 
                    ? "bg-green-500 text-white" 
                    : newsletterStatus === "error"
                    ? "bg-red-500 text-white"
                    : "bg-gradient-to-r from-orange-500 to-yellow-500 text-white hover:opacity-90"
                }`}
                data-testid="newsletter-submit"
                disabled={newsletterStatus === "success"}
              >
                {newsletterStatus === "success" 
                  ? (language === 'fr' ? '✓ Inscrit !' : '✓ Subscribed!')
                  : newsletterStatus === "error"
                  ? (language === 'fr' ? 'Erreur, réessayez' : 'Error, try again')
                  : `${t("footer_newsletter_button")} 🚀`}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="calli-container py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <p>{t("footer_copyright")}</p>
            <div className="flex items-center gap-4">
              <a href="/confidentialite" className="hover:text-white transition-colors">{t("footer_privacy")}</a>
              <a href="/cgv" className="hover:text-white transition-colors">{t("footer_terms")}</a>
              <a href="/remboursement" className="hover:text-white transition-colors">{t("footer_refund")}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
